import "./styles/app.css";
import "./styles/date-selector.css";
import { createContext, useEffect, useState } from "react";
import { eventResultStatusCodesMapfre, routes } from "./utils/symbols";
import { BookingForm } from "./pages/BookingForm";
import { Intro } from "./pages/Intro";
import { SelectCenter } from "./pages/SelectCenter";
import { DamageGlass } from "./pages/DamageGlass";
import { DamageType } from "./pages/DamageType";
import { DamageArea } from "./pages/DamageArea";
import { SelectDateTime } from "./pages/SelectDateTime";
import { BookingEnd } from "./pages/BookingEnd";
import { ThankYouPage } from "./pages/ThankYouPage";
import { Loader } from "./pages/Loader";
import { sendClarityEvent, sendGTMEvent } from "./utils/analytics";
import { ContactForm } from "./pages/ContactForm";
import { ErrorPage } from "./pages/ErrorPage";
import { sendEventToParent } from "./utils/helpers";

export const AppContext = createContext();

function App() {
	const FLOW = process.env.REACT_APP_FLOW || "web";
	const [route, navigate] = useState(routes.intro);

	const [data, setData] = useState(() => {
		return sessionStorage.getItem("Data")
			? JSON.parse(sessionStorage.getItem("Data"))
			: {};
	});

	const [centers, setCenters] = useState([]);
	const [filteredCenters, setFilteredCenters] = useState([]);
	const [visibleCenters, setVisibleCenters] = useState([]);

	const [companies, setCompanies] = useState([]);

	const [brands, setBrands] = useState([]);
	const [models, setModels] = useState([]);

	const [isTransitioning, setIsTransitioning] = useState(false);

	const [popUpClientEdit, setPopUpClientEdit] = useState(false);
	const [popUpCall, setPopUpCall] = useState(false);
	const [viewMap, setViewMap] = useState(null);

	const [errors, setErrors] = useState({});

	const openPopUpClientEdit = () => {
		setPopUpClientEdit(true);
	};

	const closePopUpClientEdit = () => {
		setPopUpClientEdit(false);
	};

	const openPopUpCall = () => {
		setPopUpCall(true);
	};

	const closePopUpCall = () => {
		setPopUpCall(false);
	};

	const validateForm = () => {
		let newErrors = {};

		const modernFormat = /^[0-9]{4}[BCDFGHJKLMNPRSTVWXYZ]{3}$/i;
		const oldFormat = /^[A-Z]{1,2}\d{4}[A-Z]{0,2}$/i;
		const veryOldFormat = /^[A-Z]{1,2}\d{1,6}$/i;

		if (route === routes.bookingForm) {
			if (
				(data.userName &&
					!/^[A-Za-zÁÉÍÓÚáéíóúÑñ' -]{2,50}$/.test(
						data.userName.trim()
					)) ||
				(data.name &&
					!/^[A-Za-zÁÉÍÓÚáéíóúÑñ' -]{2,50}$/.test(data.name.trim()))
			)
				newErrors.name = "Nombre no válido";
			if (
				(data.phoneNumber && !/^\d{9}$/.test(data.phoneNumber)) ||
				(data.phone && !/^\d{9}$/.test(data.phone))
			)
				newErrors.phone = "El teléfono debe tener 9 dígitos";
			if (
				(data.userEmail && !/^\S+@\S+\.\S+$/.test(data.userEmail)) ||
				(data.email && !/^\S+@\S+\.\S+$/.test(data.email))
			)
				newErrors.email = "Email no válido";
			if (
				(data.plateNumber &&
					!(
						modernFormat.test(data.plateNumber) ||
						oldFormat.test(data.plateNumber) ||
						veryOldFormat.test(data.plateNumber)
					)) ||
				(data.plate &&
					!(
						modernFormat.test(data.plate) ||
						oldFormat.test(data.plate) ||
						veryOldFormat.test(data.plate)
					))
			)
				newErrors.plate = "Matrícula no válida";
			if (data.workCenterCode && !data.workCenterCode)
				newErrors.workCenterCode = "Debes seleccionar un taller";
			if (data.brand && !data.brand.trim())
				newErrors.brand = "La marca es obligatoria";
			if (data.model && !data.model.trim())
				newErrors.model = "El modelo es obligatorio";
		} else if (route === routes.contactForm) {
			if (
				data.name &&
				!/^[A-Za-zÁÉÍÓÚáéíóúÑñ' -]{2,50}$/.test(data.name.trim())
			)
				newErrors.name = "Nombre no válido";
			if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
				newErrors.email = "Email no válido";
			if (data.phone && !/^\d{9}$/.test(data.phone))
				newErrors.phone = "El teléfono debe tener 9 dígitos";
			if (
				data.postalCode &&
				!/^(0[1-9]|[1-4][0-9]|5[0-2])\d{3}$/.test(data.postalCode)
			)
				newErrors.postalCode = "Código postal no válido";
			if (data.message && !data.message.trim())
				newErrors.message = "El mensaje es obligatorio";
		} else {
			if (
				data.name &&
				!/^[A-Za-zÁÉÍÓÚáéíóúÑñ' -]{2,50}$/.test(data.name.trim())
			)
				newErrors.name = "Nombre no válido";
			if (data.phone && !/^\d{9}$/.test(data.phone))
				newErrors.phone = "El teléfono debe tener 9 dígitos";
			if (data.email && !/^\S+@\S+\.\S+$/.test(data.email))
				newErrors.email = "Email no válido";
			if (
				data.plate &&
				!(
					modernFormat.test(data.plate) ||
					oldFormat.test(data.plate) ||
					veryOldFormat.test(data.plate)
				)
			)
				newErrors.plate = "Matrícula no válida";
			if (!data.noInsurance && !data.insurance) {
				newErrors.insurance =
					"Selecciona una aseguradora o marca que no tienes";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const locator = params.get("locator");

		const origin = params.get("origin");
		// console.log(origin);
		if (origin) {
			setData((prevData) => ({
				...prevData,
				origin: origin,
			}));
		}

		if (!locator) {
			navigate(routes.bookingForm);
		} else {
			setData((prevData) => ({
				...prevData,
				locator,
			}));
		}

		const popUpCall = params.get("popUpCall");
		const contactForm = params.get("contactForm");

		const location = params.get("location");

		if (location || location === "") {
			navigate(routes.selectCenter);
			setViewMap(location);
		}

		if (popUpCall) {
			openPopUpCall();
		} else if (contactForm) {
			navigate(routes.contactForm);
		} else if (!location) {
			sendClarityEvent("fc-iniciar-cita");
			sendGTMEvent("fc-iniciar-cita", {
				origin: data.origin,
				locator: data.locator,
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// console.log(data);
		route !== routes.thankYouPage &&
			sessionStorage.setItem("Data", JSON.stringify(data));
	}, [data, route]);

	useEffect(() => {
		let API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

		let controller = new AbortController();
		let options = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
			signal: controller.signal,
		};

		if (data.locator) {
			fetch(`${API_URL}/bookings/${data.locator}`, options)
				.then((res) => res.json())
				.then((newData) => {
					if (newData.result === "ok") {
						setData((prevData) => ({
							...prevData,
							...newData.booking,
							origin: prevData.origin,
						}));
					}
				})
				.catch((err) => {
					console.error(err);
					sendEventToParent(
						eventResultStatusCodesMapfre.ERROR_5XX,
						data,
						FLOW
					);
				})
				.finally(() => {
					controller.abort();
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.locator]);

	useEffect(() => {
		if (data.centerId && centers.length > 0) {
			const selectedCenter = centers.find(
				(center) => center.id === data.centerId
			);
			if (selectedCenter) {
				setData((prevData) => ({
					...prevData,
					centerName: selectedCenter.name,
					centerCode: selectedCenter.code,
				}));
			}
		}
	}, [data.centerId, centers]);

	useEffect(() => {
		setIsTransitioning(true);
	}, [data.date, route, setIsTransitioning]);

	return (
		<div
			className={
				"app" +
				(FLOW === "web" ? " web" : FLOW === "mapfre" ? " mapfre" : "") +
				" " +
				route +
				(viewMap || viewMap === "" ? " site_map" : "")
			}
		>
			<AppContext.Provider
				value={{
					data,
					setData,
					centers,
					setCenters,
					filteredCenters,
					setFilteredCenters,
					visibleCenters,
					setVisibleCenters,
					companies,
					setCompanies,
					brands,
					setBrands,
					models,
					setModels,
					route,
					navigate,
					popUpClientEdit,
					openPopUpClientEdit,
					closePopUpClientEdit,
					popUpCall,
					openPopUpCall,
					closePopUpCall,
					isTransitioning,
					setIsTransitioning,
					errors,
					validateForm,
					FLOW,
					viewMap,
				}}
			>
				{route === routes.bookingForm ? (
					<BookingForm />
				) : route === routes.intro ? (
					<Intro />
				) : route === routes.selectCenter ? (
					<SelectCenter />
				) : route === routes.damageGlass ? (
					<DamageGlass />
				) : route === routes.damageType ? (
					<DamageType />
				) : route === routes.damageArea ? (
					<DamageArea />
				) : route === routes.selectDateTime ? (
					<SelectDateTime />
				) : route === routes.bookingEnd ? (
					<BookingEnd />
				) : route === routes.thankYouPage ? (
					<ThankYouPage />
				) : route === routes.contactForm ? (
					<ContactForm />
				) : route === routes.errorPage ? (
					<ErrorPage />
				) : (
					<Loader />
				)}
			</AppContext.Provider>
		</div>
	);
}

export default App;
